import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, ArrowLeft, Building2, Loader2, MapPin } from 'lucide-react'
import { getPendingApprovalsCount } from 'modules/permit/api/permitService'
import { getActiveTenants, type Tenant } from 'shared/api/tenantService'
import { Button } from 'shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shared/ui/dialog'

export interface Site {
  id: string
  name: string
  description: string
  location: string
  activeUsers: number
}

interface SiteSelectorProps {
  isOpen: boolean
  onSelectSite: (site: Site) => void
  sites?: Site[]
  onBack?: () => void
  userId?: string
}

export function SiteSelector({ isOpen, onSelectSite, sites, onBack, userId }: SiteSelectorProps) {
  const [selectedSiteId, setSelectedSiteId] = useState<string>('')
  const [availableSites, setAvailableSites] = useState<Site[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({})

  const formatTenant = useCallback(
    (tenant: Tenant): Site => ({
      id: tenant.tenantId,
      name: tenant.name ?? 'ไม่ระบุชื่อไซต์',
      description: tenant.description ?? 'ยังไม่มีรายละเอียดสำหรับไซต์นี้',
      location: tenant.location ?? 'ประเทศไทย',
      activeUsers: tenant.activeUsers ?? 0,
    }),
    [],
  )

  const fetchActiveTenants = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const tenants = await getActiveTenants()
      setAvailableSites(tenants.map(formatTenant))
    } catch (fetchError) {
      console.error('Error loading sites:', fetchError)
      setError('ไม่สามารถโหลดรายการไซต์ได้')
      setAvailableSites([])
    } finally {
      setLoading(false)
    }
  }, [formatTenant])

  const hydrateSeedSites = useCallback(
    async (seedSites: Site[]) => {
      try {
        setLoading(true)
        setError(null)

        const tenants = await getActiveTenants()
        const tenantLookup = new Map(
          tenants.map((tenant) => [tenant.tenantId, formatTenant(tenant)]),
        )

        setAvailableSites(
          seedSites.map((site) => {
            const hydrated = tenantLookup.get(site.id)
            if (!hydrated) {
              return site
            }

            return {
              ...hydrated,
              id: site.id,
              name: site.name || hydrated.name,
              description: site.description || hydrated.description,
              location: site.location || hydrated.location,
            }
          }),
        )
      } catch (hydrateError) {
        console.error('Error hydrating sites:', hydrateError)
        setAvailableSites(seedSites)
      } finally {
        setLoading(false)
      }
    },
    [formatTenant],
  )

  useEffect(() => {
    if (sites && sites.length > 0) {
      void hydrateSeedSites(sites)
      return
    }

    if (isOpen) {
      void fetchActiveTenants()
    }
  }, [fetchActiveTenants, hydrateSeedSites, isOpen, sites])

  useEffect(() => {
    if (availableSites.length === 0) return
    const fetchCounts = async () => {
      const entries = await Promise.all(
        availableSites.map(async (site) => {
          try {
            const count = await getPendingApprovalsCount(site.id, userId)
            return [site.id, count] as const
          } catch {
            return [site.id, 0] as const
          }
        }),
      )
      setPendingCounts(Object.fromEntries(entries))
    }
    void fetchCounts()
  }, [availableSites, userId])

  const handleSiteSelection = () => {
    const selectedSite = availableSites.find((site) => site.id === selectedSiteId)
    if (selectedSite) {
      onSelectSite(selectedSite)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl"
        onPointerDownOutside={(event: any) => event.preventDefault()}
      >
        {onBack ? (
          <div className="mb-2">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              กลับไปเลือกวิธีเข้าสู่ระบบ
            </Button>
          </div>
        ) : null}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            เลือกสถานประกอบการ
          </DialogTitle>
          <DialogDescription>
            โปรดเลือกไซต์ที่คุณต้องการใช้งานร่วมกับระบบ E-PTW
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">กำลังโหลดรายการไซต์...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="mb-4 h-12 w-12 text-red-500" />
              <p className="font-medium text-red-600">{error}</p>
              <p className="mt-2 text-sm text-gray-500">
                โปรดตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง
              </p>
              <Button variant="outline" onClick={() => void fetchActiveTenants()} className="mt-4">
                ลองอีกครั้ง
              </Button>
            </div>
          ) : availableSites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Building2 className="mb-4 h-12 w-12 text-gray-400" />
              <p className="font-medium text-gray-600">ยังไม่มีไซต์ที่พร้อมให้เลือก</p>
              <p className="mt-2 text-sm text-gray-500">
                โปรดตรวจสอบการตั้งค่า หรือสร้างไซต์ใหม่ในระบบหลังบ้าน
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {availableSites.map((site) => (
                <div
                  key={site.id}
                  className={`cursor-pointer rounded-lg border p-4 transition-all hover:border-blue-500 hover:shadow-md ${
                    selectedSiteId === site.id
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200'
                  }`}
                  onClick={() => setSelectedSiteId(site.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Building2 className="h-8 w-8 text-blue-600" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900">{site.name}</h3>
                      <p className="mt-1 text-sm text-gray-600">{site.description}</p>

                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <MapPin className="h-4 w-4" />
                          {site.location || 'ประเทศไทย'}
                        </div>
                        {(pendingCounts[site.id] ?? 0) > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-red-600">รายการรออนุมัติ</span>
                            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                              {pendingCounts[site.id]}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <div
                        className={`h-4 w-4 rounded-full border-2 ${
                          selectedSiteId === site.id
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedSiteId === site.id ? (
                          <div className="h-full w-full rounded-full border-2 border-blue-600 bg-white" />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && availableSites.length > 0 ? (
            <div className="flex justify-end pt-4">
              <Button onClick={handleSiteSelection} disabled={!selectedSiteId} className="min-w-[120px]">
                ยืนยัน
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
